import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore } from 'firebase/firestore';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedSwitch } from './ThemedSwitch';
import { useCalendarGlassStyles } from './calendarGlassStyles';
import {
  CALENDAR_PERMISSION_TABS_VISIBLE,
  PERMISSION_LEVELS,
  getMemberPermissionsEntry,
  normalizeMemberPermissionsEntry,
  saveMemberPermissionEntry,
  saveSetlistModerator,
} from '../../utils/tourMemberPermissionsService';

const PERMISSION_OPTIONS = [
  { id: PERMISSION_LEVELS.NONE, label: 'Hidden' },
  { id: PERMISSION_LEVELS.VIEW, label: 'View' },
  { id: PERMISSION_LEVELS.EDIT, label: 'Edit' },
];

function PermissionLevelPicker({ tabKey, value, onChange, theme, disabled }) {
  return (
    <View style={pickerStyles.row}>
      <Text style={[pickerStyles.tabLabel, { color: theme.text.secondary }]} numberOfLines={1}>
        {tabKey}
      </Text>
      <View style={pickerStyles.options}>
        {PERMISSION_OPTIONS.map((opt) => {
          const active = value === opt.id;
          return (
            <TouchableOpacity
              key={opt.id}
              style={[
                pickerStyles.option,
                { borderColor: theme.ui.border },
                active && { backgroundColor: theme.brand.primary, borderColor: theme.brand.primary },
              ]}
              onPress={() => onChange(tabKey, opt.id)}
              disabled={disabled}
              accessibilityLabel={`${tabKey}: ${opt.label}`}
            >
              <Text
                style={[
                  pickerStyles.optionText,
                  { color: theme.text.secondary },
                  active && { color: theme.button.primaryText },
                ]}
              >
                {opt.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

export function MemberPermissionsEditor({
  entry,
  theme,
  isDarkMode,
  saving,
  onChangeTab,
  onToggleParty,
  onToggleShares,
  onToggleSetlistModerator,
  isSetlistModerator = false,
  showSetlistModerator = false,
  readOnly = false,
  readOnlyMessage = '',
}) {
  const styles = useMemo(() => createEditorStyles(theme, isDarkMode), [theme, isDarkMode]);
  const disabled = saving || readOnly;
  return (
    <View style={styles.editor}>
      {readOnly && readOnlyMessage ? (
        <Text style={[styles.readOnlyBanner, { color: theme.text.secondary, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }]}>
          {readOnlyMessage}
        </Text>
      ) : null}
      <View style={styles.toggleRow}>
        <View style={styles.toggleCopy}>
          <Text style={styles.toggleTitle}>Manage tour party</Text>
          <Text style={styles.toggleHint}>Send invites and remove members</Text>
        </View>
        <ThemedSwitch value={entry.canManageParty} onValueChange={onToggleParty} disabled={disabled} />
      </View>
      <View style={styles.toggleRow}>
        <View style={styles.toggleCopy}>
          <Text style={styles.toggleTitle}>Manage share links</Text>
          <Text style={styles.toggleHint}>Activate links and control who sees shared days</Text>
        </View>
        <ThemedSwitch value={entry.canManageShares} onValueChange={onToggleShares} disabled={disabled} />
      </View>
      {showSetlistModerator ? (
        <View style={styles.toggleRow}>
          <View style={styles.toggleCopy}>
            <Text style={styles.toggleTitle}>Setlist moderator</Text>
            <Text style={styles.toggleHint}>Final say on song order and BPM. Tour owner can always publish too.</Text>
          </View>
          <ThemedSwitch value={isSetlistModerator} onValueChange={onToggleSetlistModerator} disabled={disabled} />
        </View>
      ) : null}
      <Text style={styles.sectionLabel}>Calendar sections</Text>
      <Text style={styles.sectionHint}>
        Hidden = no access · View = read only · Edit = can change content
      </Text>
      <View style={styles.tabGrid}>
        {CALENDAR_PERMISSION_TABS_VISIBLE.map((tabKey) => (
          <PermissionLevelPicker
            key={tabKey}
            tabKey={tabKey}
            value={entry.tabs?.[tabKey] || PERMISSION_LEVELS.NONE}
            onChange={onChangeTab}
            theme={theme}
            disabled={disabled}
          />
        ))}
      </View>
    </View>
  );
}

export function TourPartyPermissionsPanel({
  ownerId,
  currentUserId,
  tourId,
  tourShareSettings,
  acceptedMembers = [],
  theme,
  onError,
  onSuccess,
  defaultOpen = false,
}) {
  const db = getFirestore();
  const { isDarkMode } = useTheme();
  const glassStyles = useCalendarGlassStyles(theme, isDarkMode);
  const styles = useMemo(() => createPanelStyles(theme, isDarkMode), [theme, isDarkMode]);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [savingMemberId, setSavingMemberId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(defaultOpen);

  const isOwner = Boolean(currentUserId && ownerId && currentUserId === ownerId);
  const membersWithAccounts = useMemo(
    () =>
      acceptedMembers.filter(
        (m) =>
          String(m.status || '').toLowerCase() === 'accepted' &&
          m.onPlatform &&
          String(m.id || '').trim()
      ),
    [acceptedMembers]
  );

  const persistEntry = useCallback(
    async (memberUid, nextEntry) => {
      if (!isOwner || !tourId) return;
      if (memberUid === ownerId) {
        onError?.('Tour creator permissions cannot be changed.');
        return;
      }
      try {
        setSavingMemberId(memberUid);
        await saveMemberPermissionEntry(db, ownerId, tourId, memberUid, nextEntry, tourShareSettings || {});
        onSuccess?.('Permissions updated.');
      } catch (err) {
        console.error('saveMemberPermissionEntry', err);
        onError?.(err?.message || 'Could not save permissions.');
      } finally {
        setSavingMemberId(null);
      }
    },
    [db, isOwner, tourId, ownerId, tourShareSettings, onError, onSuccess]
  );

  const handleTabChange = useCallback(
    (memberUid, currentEntry) => (tabKey, level) => {
      const nextEntry = normalizeMemberPermissionsEntry({
        ...currentEntry,
        tabs: { ...currentEntry.tabs, [tabKey]: level },
      });
      persistEntry(memberUid, nextEntry);
    },
    [persistEntry]
  );

  const handleToggleParty = useCallback(
    (memberUid, currentEntry) => (value) => {
      persistEntry(memberUid, { ...currentEntry, canManageParty: value });
    },
    [persistEntry]
  );

  const handleToggleShares = useCallback(
    (memberUid, currentEntry) => (value) => {
      persistEntry(memberUid, { ...currentEntry, canManageShares: value });
    },
    [persistEntry]
  );

  if (!isOwner) return null;

  return (
    <View style={glassStyles.card}>
      <TouchableOpacity
        style={styles.header}
        onPress={() => setSettingsOpen((v) => !v)}
        activeOpacity={0.85}
      >
        <View style={styles.headerLeft}>
          <Ionicons name="shield-checkmark-outline" size={16} color={theme.brand.primary} />
          <Text style={styles.title}>Member permissions</Text>
        </View>
        <Ionicons
          name={settingsOpen ? 'chevron-up' : 'chevron-down'}
          size={16}
          color={theme.text.tertiary}
        />
      </TouchableOpacity>

      {settingsOpen ? (
        <>
          <Text style={styles.lead}>
            Choose what each accepted tour party member can see and edit on this tour&apos;s calendar.
          </Text>
          {membersWithAccounts.length === 0 ? (
            <Text style={styles.emptyHint}>
              Accepted members with VRC accounts will appear here so you can set their access.
            </Text>
          ) : (
            membersWithAccounts.map((member) => {
              const memberUid = String(member.id || '').trim();
              const label = member.name || member.email || 'Member';
              const expanded = expandedMemberId === memberUid;
              const entry = getMemberPermissionsEntry(
                tourShareSettings || {},
                memberUid,
                tourShareSettings?.managerIds
              );
              const saving = savingMemberId === memberUid;
              return (
                <View key={memberUid} style={styles.memberBlock}>
                  <TouchableOpacity
                    style={styles.memberHeader}
                    onPress={() => setExpandedMemberId(expanded ? null : memberUid)}
                    activeOpacity={0.85}
                  >
                    <View style={styles.memberHeaderCopy}>
                      <Text style={styles.memberName} numberOfLines={1}>
                        {label}
                      </Text>
                      <Text style={styles.memberMeta} numberOfLines={1}>
                        {Object.values(entry.tabs || {}).filter((v) => v === PERMISSION_LEVELS.EDIT).length} edit
                        {' · '}
                        {Object.values(entry.tabs || {}).filter((v) => v === PERMISSION_LEVELS.VIEW).length} view
                      </Text>
                    </View>
                    {saving ? (
                      <ActivityIndicator size="small" color={theme.brand.accent} />
                    ) : (
                      <Ionicons
                        name={expanded ? 'chevron-up' : 'chevron-down'}
                        size={14}
                        color={theme.text.tertiary}
                      />
                    )}
                  </TouchableOpacity>
                  {expanded ? (
                    <MemberPermissionsEditor
                      entry={entry}
                      theme={theme}
                      isDarkMode={isDarkMode}
                      saving={saving}
                      onChangeTab={handleTabChange(memberUid, entry)}
                      onToggleParty={handleToggleParty(memberUid, entry)}
                      onToggleShares={handleToggleShares(memberUid, entry)}
                      showSetlistModerator={memberUid !== ownerId}
                      isSetlistModerator={String(tourShareSettings?.setlistModeratorId || '') === memberUid}
                      onToggleSetlistModerator={(value) => {
                        saveSetlistModerator(db, ownerId, tourId, value ? memberUid : '', tourShareSettings || {})
                          .then(() => onSuccess?.('Setlist moderator updated.'))
                          .catch((err) => onError?.(err?.message || 'Could not save setlist moderator.'));
                      }}
                    />
                  ) : null}
                </View>
              );
            })
          )}
        </>
      ) : (
        <Text style={styles.collapsedHint}>
          Set view and edit access for tour party members.
        </Text>
      )}
    </View>
  );
}

const pickerStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    minHeight: 32,
  },
  tabLabel: {
    flex: 1,
    fontSize: 12,
    fontWeight: '600',
    minWidth: 88,
  },
  options: {
    flexDirection: 'row',
    gap: 6,
    flexShrink: 0,
  },
  option: {
    minWidth: 64,
    height: 28,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  optionText: {
    fontSize: 12,
    fontWeight: '600',
  },
});

function createEditorStyles(theme, isDarkMode) {
  const inset = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  return StyleSheet.create({
    editor: {
      gap: 8,
      paddingTop: 8,
      paddingHorizontal: 8,
      paddingBottom: 4,
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
      backgroundColor: inset,
      borderRadius: 8,
    },
    toggleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      paddingVertical: 4,
    },
    toggleCopy: { flex: 1, minWidth: 0 },
    toggleTitle: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.text.primary,
    },
    toggleHint: {
      fontSize: 11,
      color: theme.text.secondary,
      marginTop: 2,
      lineHeight: 15,
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.primary,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: 4,
    },
    sectionHint: {
      fontSize: 11,
      color: theme.text.secondary,
      lineHeight: 15,
      marginBottom: 2,
    },
    tabGrid: { gap: 4 },
    readOnlyBanner: {
      fontSize: 11,
      lineHeight: 16,
      paddingHorizontal: 10,
      paddingVertical: 8,
      borderRadius: 8,
      marginBottom: 4,
    },
  });
}

function createPanelStyles(theme, isDarkMode) {
  return StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flex: 1,
      minWidth: 0,
    },
    title: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.text.primary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    lead: {
      fontSize: 12,
      lineHeight: 17,
      color: theme.text.secondary,
      marginTop: 8,
    },
    collapsedHint: {
      fontSize: 11,
      color: theme.text.tertiary,
      marginTop: 4,
    },
    emptyHint: {
      fontSize: 12,
      lineHeight: 16,
      color: theme.text.secondary,
      marginTop: 8,
    },
    memberBlock: {
      marginTop: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : theme.background.secondary,
    },
    memberHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 10,
    },
    memberHeaderCopy: { flex: 1, minWidth: 0 },
    memberName: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.text.primary,
    },
    memberMeta: {
      fontSize: 11,
      color: theme.text.tertiary,
      marginTop: 2,
    },
  });
}

export default TourPartyPermissionsPanel;
